import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const LINKS = [
  { label: "Leistungen", href: "#leistungen" },
  { label: "Über mich", href: "#ueber-uns" },
  { label: "Kontakt", href: "#kontakt" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/70 border-b border-zinc-200/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center">
          <img src={LOGO_URL} alt="inn'spective" className="h-10 w-auto object-contain" />
        </a>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s|ü/g, "")}`}
              className="text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors duration-200 relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 brand-gradient group-hover:w-full transition-all duration-300" />
            </a>
          ))}
          <a
            href="#kontakt"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-zinc-950 text-white text-sm font-medium pl-5 pr-4 py-2.5 hover:bg-zinc-800 transition-colors duration-200"
          >
            Projekt anfragen
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-zinc-900"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menü"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden backdrop-blur-xl bg-white/90 border-b border-zinc-200"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s|ü/g, "")}`}
                  className="text-lg font-medium text-zinc-800"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#kontakt"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-zinc-950 text-white text-sm font-medium px-5 py-3"
              >
                Projekt anfragen <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
